/* SP VENUE 3D — stylised, code-built 3D model of the National Stadium at
   The Kallang + surrounding precinct, with HTML hotspots and Spark as guide.
   Mounted by sections/sp-venue-3d.liquid. Each root has a JSON config island.
   Three.js (r128 UMD) is loaded lazily when the section scrolls into view. */
(function () {
  'use strict';

  var THREE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  var threeLoading = null;
  function loadThree() {
    if (window.THREE) return Promise.resolve(window.THREE);
    if (threeLoading) return threeLoading;
    threeLoading = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = THREE_URL; s.async = true;
      s.onload = function () { res(window.THREE); };
      s.onerror = rej;
      document.head.appendChild(s);
    });
    return threeLoading;
  }

  /* ---------- venue geometry constants (metres, y up, -z = north) ---------- */
  var DOME_H = 82, DOME_A = 155;                       // height, base radius
  var DOME_R = (DOME_A * DOME_A + DOME_H * DOME_H) / (2 * DOME_H);
  var DOME_CY = DOME_H - DOME_R;                       // sphere centre y
  var SHELL_Y0 = 20;                                   // shell starts above lattice band
  var OPEN_W = 46, OPEN_Z = 112;                       // roof opening half-width / half-length
  var PANEL_W = 66;                                    // movable roof leaf width
  function shore(x) { return 188 + 38 * Math.sin(x / 210) + (x > 250 ? (x - 250) * 0.25 : 0); }

  /* Named anchors: where a hotspot sits + how the camera frames it. */
  /* Layout is stylised/approximate — positions are editable per hotspot (anchor "custom"). */
  var ANCHORS = {
    stadium:     { p: [0, 86, 0],       cam: [250, 190, 300] },
    pitch:       { p: [0, 3, 0],        cam: [0, 30, 96], inside: true },
    seats:       { p: [0, 18, -103],    cam: [26, 22, 62], inside: true },
    promenade:   { p: [-118, 3, 118],   cam: [-250, 70, 270] },
    arena:       { p: [-215, 24, -150], cam: [-420, 150, -330] },
    aquatic:     { p: [-215, 26, 40],   cam: [-440, 150, 210] },
    ocbcsquare:  { p: [-118, 2, -95],   cam: [-220, 110, -40] },
    indoor:      { p: [372, 44, 120],   cam: [560, 170, 330] },
    mall:        { p: [246, 22, 34],    cam: [470, 140, 200] },
    mrt:         { p: [300, 12, -88],   cam: [470, 110, -60] },
    trbridge:    { p: [505, 8, 140],    cam: [640, 110, 330] },
    trmrt:       { p: [610, 12, 250],   cam: [760, 120, 420] },
    athletics:   { p: [-250, 3, -330],  cam: [-430, 150, -520] },
    football:    { p: [-30, 3, -340],   cam: [-120, 150, -560] },
    tennis:      { p: [170, 3, -335],   cam: [300, 140, -540] },
    watersports: { p: [-150, 8, 160],   cam: [-260, 70, 360] }
  };
  ANCHORS.roof = ANCHORS.stadium;
  // Geylang River channel: runs from the basin mouth north-east, east of the Indoor Stadium
  var RIVER = [[470, 260], [500, 120], [540, -20], [600, -180], [680, -340], [780, -520]];
  function riverDist(x, z) {
    var best = 1e9;
    for (var i = 0; i < RIVER.length - 1; i++) {
      var a = RIVER[i], b = RIVER[i + 1], dx = b[0] - a[0], dz = b[1] - a[1];
      var t = Math.max(0, Math.min(1, ((x - a[0]) * dx + (z - a[1]) * dz) / (dx * dx + dz * dz)));
      best = Math.min(best, Math.hypot(x - a[0] - dx * t, z - a[1] - dz * t));
    }
    return best;
  }

  /* ---------- canvas textures ---------- */
  function canvasTex(THREE, w, h, draw, repeat) {
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    draw(c.getContext('2d'), w, h);
    var t = new THREE.CanvasTexture(c);
    t.encoding = THREE.sRGBEncoding;
    t.anisotropy = 4;
    if (repeat) { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(repeat[0], repeat[1]); }
    return t;
  }

  function shellTextures(THREE, accent) {
    // top-down projected: diamond truss lines + dotted LED lines (emissive)
    var map = canvasTex(THREE, 1024, 1024, function (g, w, h) {
      g.fillStyle = '#8f9aa6'; g.fillRect(0, 0, w, h);
      for (var i = 0; i < w; i += 8) { g.fillStyle = i % 16 ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.06)'; g.fillRect(i, 0, 4, h); }
      g.strokeStyle = 'rgba(255,255,255,.55)'; g.lineWidth = 3;
      var cx = w / 2, cy = h / 2;
      for (var k = 0; k < 28; k++) {           // radial diamond zig-zag ring
        var a0 = k / 28 * Math.PI * 2, a1 = (k + 0.5) / 28 * Math.PI * 2;
        g.beginPath();
        g.moveTo(cx + Math.cos(a0) * 500, cy + Math.sin(a0) * 500);
        g.lineTo(cx + Math.cos(a1) * 330, cy + Math.sin(a1) * 330);
        g.lineTo(cx + Math.cos(a0 + Math.PI * 2 / 28) * 500, cy + Math.sin(a0 + Math.PI * 2 / 28) * 500);
        g.stroke();
      }
    });
    var emi = canvasTex(THREE, 1024, 1024, function (g, w, h) {
      g.fillStyle = '#000'; g.fillRect(0, 0, w, h);
      var cx = w / 2, cy = h / 2;
      function dots(x0, y0, x1, y1, n, col) {
        for (var i = 0; i <= n; i++) {
          var t = i / n; g.fillStyle = col;
          g.beginPath(); g.arc(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, 3.2, 0, 7); g.fill();
        }
      }
      for (var k = 0; k < 28; k++) {
        var a0 = k / 28 * Math.PI * 2, a1 = (k + 0.5) / 28 * Math.PI * 2, a2 = (k + 1) / 28 * Math.PI * 2;
        var col = k % 2 ? '#ffffff' : accent;
        dots(cx + Math.cos(a0) * 500, cy + Math.sin(a0) * 500, cx + Math.cos(a1) * 330, cy + Math.sin(a1) * 330, 18, col);
        dots(cx + Math.cos(a1) * 330, cy + Math.sin(a1) * 330, cx + Math.cos(a2) * 500, cy + Math.sin(a2) * 500, 18, col);
      }
    });
    return { map: map, emissive: emi };
  }

  /* ---------- geometry helpers ---------- */
  // Patch of the dome sphere over x∈[x0,x1], z∈[zLo(x), zHi(x)] (top-projected UVs).
  function capPatch(THREE, R, cy, x0, x1, zLo, zHi, nx, nz) {
    var pos = [], uv = [], idx = [];
    for (var i = 0; i <= nx; i++) {
      var x = x0 + (x1 - x0) * i / nx, lo = zLo(x), hi = zHi(x);
      for (var j = 0; j <= nz; j++) {
        var z = lo + (hi - lo) * j / nz;
        var y = cy + Math.sqrt(Math.max(R * R - x * x - z * z, 0));
        pos.push(x, y, z); uv.push((x + DOME_A) / (2 * DOME_A), (z + DOME_A) / (2 * DOME_A));
      }
    }
    for (i = 0; i < nx; i++) for (j = 0; j < nz; j++) {
      var a = i * (nz + 1) + j, b = a + nz + 1;
      idx.push(a, a + 1, b, b, a + 1, b + 1);
    }
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx); g.computeVertexNormals();
    return g;
  }
  // radius of the dome's horizontal section at height y
  function domeRAt(y) { var d = y - DOME_CY; return Math.sqrt(Math.max(DOME_R * DOME_R - d * d, 0)); }

  // superellipse point (squarer bowl)
  function sePt(ax, az, t) {
    var c = Math.cos(t), s = Math.sin(t), n = 2.6;
    return [ax * Math.sign(c) * Math.pow(Math.abs(c), 2 / n), az * Math.sign(s) * Math.pow(Math.abs(s), 2 / n)];
  }
  // ring between two superellipses at two heights (seat tier, wall, track...)
  function bowlRing(THREE, in0, out0, seg, uRep) {
    var pos = [], uv = [], idx = [];
    for (var k = 0; k <= seg; k++) {
      var t = k / seg * Math.PI * 2;
      var p0 = sePt(in0[0], in0[1], t), p1 = sePt(out0[0], out0[1], t);
      pos.push(p0[0], in0[2], p0[1], p1[0], out0[2], p1[1]);
      uv.push(k / seg * (uRep || 1), 0, k / seg * (uRep || 1), 1);
    }
    for (k = 0; k < seg; k++) { var a = k * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx); g.computeVertexNormals();
    return g;
  }
  // flat ribbon along a polyline
  function ribbon(THREE, pts, width, y) {
    var pos = [], idx = [], uv = [], len = 0;
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i], q = pts[Math.min(i + 1, pts.length - 1)], r = pts[Math.max(i - 1, 0)];
      var dx = q[0] - r[0], dz = q[1] - r[1], L = Math.hypot(dx, dz) || 1;
      var nx = -dz / L * width / 2, nz = dx / L * width / 2;
      if (i) len += Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1]);
      pos.push(p[0] + nx, y, p[1] + nz, p[0] - nx, y, p[1] - nz); uv.push(len / 20, 0, len / 20, 1);
    }
    for (i = 0; i < pts.length - 1; i++) { var a = i * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx); g.computeVertexNormals();
    return g;
  }
  function mulberry(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }

  /* ======================================================================== */
  function Venue(root) {
    this.root = root;
    this.cfg = JSON.parse(root.querySelector('[data-venue-config]').textContent);
    this.stage = root.querySelector('.spv-stage');
    this.hotspots = (this.cfg.hotspots || []).map(function (h, i) {
      var a = ANCHORS[h.anchor] || null;
      var p = (h.anchor === 'custom' || !a) ? [h.x || 0, h.y || 10, h.z || 0] : a.p.slice();
      return Object.assign({}, h, { i: i, p: p, cam: a && h.anchor !== 'custom' ? a.cam : null, inside: a ? !!a.inside : false });
    });
    this.night = this.cfg.night !== false;
    this.roofOpen = this.cfg.roof !== 'closed';
    this.roofT = this.roofOpen ? 0 : 1;          // 0 open → 1 closed
    this.reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.active = -1;
    this.idleT = 0;
    this.tour = null;
  }

  Venue.prototype.boot = function () {
    var self = this;
    loadThree().then(function (THREE) {
      try {
        var c = document.createElement('canvas');
        if (!(c.getContext('webgl') || c.getContext('experimental-webgl'))) throw new Error('no webgl');
      } catch (e) { self.root.classList.add('spv-nogl'); return; }
      self.T = THREE;
      self.build();
      self.root.classList.add('spv-ready');
    }).catch(function () { self.root.classList.add('spv-nogl'); });
  };

  Venue.prototype.build = function () {
    var THREE = this.T, self = this, accent = this.cfg.accent || '#B7FF00';
    var stage = this.stage;
    var r = this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    r.outputEncoding = THREE.sRGBEncoding;
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.domElement.className = 'spv-canvas';
    stage.insertBefore(r.domElement, stage.firstChild);

    var scene = this.scene = new THREE.Scene();
    var cam = this.camera = new THREE.PerspectiveCamera(42, 1, 2, 6000);
    this.occluders = [];

    /* lights */
    this.hemi = new THREE.HemisphereLight(0xdfefff, 0x2a3324, 1);
    this.sun = new THREE.DirectionalLight(0xffffff, 1);
    this.sun.position.set(-400, 600, 300);
    this.bowlLight = new THREE.PointLight(0xffffff, 0, 420, 1.4);
    this.bowlLight.position.set(0, 60, 0);
    scene.add(this.hemi, this.sun, this.bowlLight);

    /* ground, water, plaza */
    var land = new THREE.Mesh(new THREE.PlaneGeometry(5200, 5200), this.mat('land', { color: 0x3d4a36, roughness: 1 }));
    land.rotation.x = -Math.PI / 2; land.position.y = -0.4; scene.add(land);

    var ws = new THREE.Shape(), xs = [];
    for (var x = -1700; x <= 1300; x += 25) xs.push(x);
    ws.moveTo(xs[0], -shore(xs[0]));
    xs.forEach(function (x) { ws.lineTo(x, -shore(x)); });
    ws.lineTo(1300, -560); ws.lineTo(600, -640); ws.lineTo(-200, -700); ws.lineTo(-1700, -640);
    var water = new THREE.Mesh(new THREE.ShapeGeometry(ws), this.mat('water', { color: 0x1d3f5a, roughness: 0.25, metalness: 0.4 }));
    water.rotation.x = -Math.PI / 2; water.position.y = 0.02; scene.add(water);

    var plaza = new THREE.Mesh(new THREE.CircleGeometry(182, 72), this.mat('plaza', { color: 0x8b8f8a, roughness: 0.95 }));
    plaza.rotation.x = -Math.PI / 2; plaza.position.y = 0.08; scene.add(plaza);

    /* waterfront walk + lamps along the basin edge */
    var prom = [];
    for (x = -620; x <= 440; x += 15) prom.push([x, shore(x) - 9]);
    var promMesh = new THREE.Mesh(ribbon(THREE, prom, 10, 0.25), this.mat('prom', { color: 0xbab3a6, roughness: 0.9 }));
    scene.add(promMesh);
    var lampPos = [];
    for (x = -620; x <= 440; x += 14) lampPos.push(x, 4, shore(x) - 14);

    /* Geylang River channel + Tanjong Rhu footbridge */
    var river = new THREE.Mesh(ribbon(THREE, RIVER, 70, 0.04), this.mats.water);
    scene.add(river);
    var brMat = this.mat('bridge', { color: 0xeef1f4, metalness: 0.4, roughness: 0.5, emissive: 0xffffff, emissiveIntensity: 0 });
    var deck = new THREE.Mesh(new THREE.BoxGeometry(110, 1.4, 7), brMat);
    deck.position.set(505, 6, 140); deck.rotation.y = -0.32; scene.add(deck);
    var arcPts = [];
    for (var ai = 0; ai <= 20; ai++) { var u = ai / 20 - 0.5; arcPts.push(new THREE.Vector3(u * 110, 6 + 16 * (1 - 4 * u * u), 0)); }
    [-3.2, 3.2].forEach(function (o) {
      var arc = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(arcPts), 30, 0.8, 6), brMat);
      arc.position.set(505 + Math.sin(-0.32) * o, 0, 140 + Math.cos(-0.32) * o); arc.rotation.y = -0.32; scene.add(arc);
    });
    for (ai = -50; ai <= 50; ai += 6) lampPos.push(505 + ai * Math.cos(-0.32), 8, 140 - ai * Math.sin(-0.32));

    /* Great Eastern Promenade — sheltered 3-lane loop hugging the stadium */
    var laneCols = [0x2f9a55, 0xc9473b, 0x3567b8];
    var R0 = domeRAt(0) + 8;
    laneCols.forEach(function (col, li) {
      var ring = new THREE.Mesh(new THREE.RingGeometry(R0 + li * 3, R0 + li * 3 + 2.8, 128), self.mat('lane' + li, { color: col, roughness: 0.9 }));
      ring.rotation.x = -Math.PI / 2; ring.position.y = 0.2; scene.add(ring);
    });
    var shelter = new THREE.Mesh(new THREE.CylinderGeometry(R0 - 2, R0 + 14, 3, 128, 1, true), this.mat('shelter', { color: 0xf4f6f8, transparent: true, opacity: 0.55, side: THREE.DoubleSide, roughness: 0.4, emissive: 0xffffff, emissiveIntensity: 0 }));
    shelter.position.y = 10.5; scene.add(shelter);
    for (ai = 0; ai < 96; ai++) { var aa = ai / 96 * Math.PI * 2; lampPos.push(Math.cos(aa) * (R0 + 6), 8.5, Math.sin(aa) * (R0 + 6)); }

    var lampGeo = new THREE.BufferGeometry(); lampGeo.setAttribute('position', new THREE.Float32BufferAttribute(lampPos, 3));
    this.lamps = new THREE.Points(lampGeo, new THREE.PointsMaterial({ color: 0xffd79a, size: 3.2, sizeAttenuation: true, transparent: true, opacity: 0.95 }));
    scene.add(this.lamps);

    /* arterial road with moving car lights (north edge) */
    var road = [];
    for (var t = 0; t <= 1; t += 0.02) road.push([-1100 + 2000 * t, -440 - 60 * Math.sin(t * Math.PI) + 120 * t]);
    this.roadPts = road;
    scene.add(new THREE.Mesh(ribbon(THREE, road, 26, 0.3), this.mat('road', { color: 0x2b2d30, roughness: 1 })));
    var carN = 90, carPos = new Float32Array(carN * 3), carCol = new Float32Array(carN * 3);
    this.cars = [];
    for (var i = 0; i < carN; i++) {
      var dir = i % 2 ? 1 : -1;
      this.cars.push({ t: Math.random(), v: (0.012 + Math.random() * 0.01) * dir, lane: dir * (4 + (i % 3) * 3) });
      var red = dir < 0; carCol.set(red ? [1, 0.25, 0.2] : [1, 0.95, 0.8], i * 3);
    }
    var carGeo = new THREE.BufferGeometry();
    carGeo.setAttribute('position', new THREE.BufferAttribute(carPos, 3));
    carGeo.setAttribute('color', new THREE.BufferAttribute(carCol, 3));
    this.carPoints = new THREE.Points(carGeo, new THREE.PointsMaterial({ size: 2.6, vertexColors: true }));
    scene.add(this.carPoints);

    this.buildStadium(accent);
    this.buildPrecinct();
    this.buildCity();

    /* hotspots + Spark DOM */
    this.hsLayer = stage.querySelector('.spv-hotspots');
    this.hotspots.forEach(function (h) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'spv-hs'; b.setAttribute('aria-label', h.title);
      b.innerHTML = '<span class="spv-hs-dot"></span><span class="spv-hs-lbl"></span>';
      b.querySelector('.spv-hs-lbl').textContent = h.title;
      b.addEventListener('click', function (e) { e.stopPropagation(); self.stopTour(); self.select(h.i); });
      self.hsLayer.appendChild(b); h.el = b;
      h.v = new THREE.Vector3(h.p[0], h.p[1], h.p[2]);
    });
    this.spark = stage.querySelector('.spv-spark');
    this.sparkImg = this.spark.querySelector('img');
    this.bubble = stage.querySelector('.spv-bubble');
    this.sparkPos = new THREE.Vector3(-40, 6, 172);
    this.sparkFrom = this.sparkPos.clone(); this.sparkTo = this.sparkPos.clone(); this.sparkHop = 1;
    this.setSparkPose(this.cfg.spark_idle);

    /* camera rig (custom orbit with damping) */
    this.tgt = new THREE.Vector3(0, 20, 0); this.tgtGoal = this.tgt.clone();
    this.sph = { r: 640, th: 0.75, ph: 1.05 }; this.sphGoal = { r: 560, th: 0.62, ph: 1.0 };
    this.home = { tgt: [0, 20, 0], r: 560, th: 0.62, ph: 1.0 };
    this.bindInput();
    this.applyLook();

    this.raycaster = new THREE.Raycaster();
    this.resize();
    this.ro = new ResizeObserver(function () { self.resize(); });
    this.ro.observe(stage);
    this.visible = true;
    new IntersectionObserver(function (es) { self.visible = es[0].isIntersecting; if (self.visible) self.loop(); }, { threshold: 0 }).observe(stage);
    this.clock = new THREE.Clock();
    this.frame = 0;
    this.say(this.cfg.intro || "Hi, I'm Spark! Tap a glowing point and I'll show you around.");
    this.loop();
  };

  Venue.prototype.mat = function (key, o) {
    var m = new this.T.MeshStandardMaterial(o);
    (this.mats || (this.mats = {}))[key] = m;
    return m;
  };

  Venue.prototype.buildStadium = function (accent) {
    var THREE = this.T, scene = this.scene, R = DOME_R, cy = DOME_CY;
    var tex = shellTextures(THREE, accent);
    var shellMat = this.mat('shell', { color: 0xffffff, map: tex.map, emissive: 0xffffff, emissiveMap: tex.emissive, emissiveIntensity: 0, metalness: 0.15, roughness: 0.5, side: THREE.DoubleSide });
    var aB = domeRAt(SHELL_Y0);
    function zmax(x) { return Math.sqrt(Math.max(aB * aB - x * x, 0)); }
    function neg(f) { return function (x) { return -f(x); }; }
    var parts = [
      capPatch(THREE, R, cy, -aB, -OPEN_W, neg(zmax), zmax, 40, 60),
      capPatch(THREE, R, cy, OPEN_W, aB, neg(zmax), zmax, 40, 60),
      capPatch(THREE, R, cy, -OPEN_W, OPEN_W, function () { return OPEN_Z; }, zmax, 16, 10),
      capPatch(THREE, R, cy, -OPEN_W, OPEN_W, neg(zmax), function () { return -OPEN_Z; }, 16, 10)
    ];
    var self = this;
    this.shell = new THREE.Group();
    parts.forEach(function (g) { var m = new THREE.Mesh(g, shellMat); self.shell.add(m); self.occluders.push(m); });
    scene.add(this.shell);

    /* movable roof leaves: patches on a slightly larger sphere, rotated about z through the sphere centre */
    var leafTex = canvasTex(THREE, 256, 256, function (g, w, h) {
      g.fillStyle = '#d9dee4'; g.fillRect(0, 0, w, h);
      for (var y = 0; y < h; y += 10) { g.fillStyle = 'rgba(70,80,95,.35)'; g.fillRect(0, y, w, 2); }
    });
    var leafMat = this.mat('leaf', { color: 0xffffff, map: leafTex, metalness: 0.2, roughness: 0.35, side: THREE.DoubleSide, emissive: 0xdfe8ff, emissiveIntensity: 0 });
    var R2 = R + 3.5, x0 = OPEN_W - 4, x1 = OPEN_W - 4 + PANEL_W;
    function lz(x) { return Math.sqrt(Math.max(aB * aB - x * x, 0)) * 0.9; }
    this.leaves = [];
    [1, -1].forEach(function (s) {
      var g = capPatch(THREE, R2, cy, x0, x1, neg(lz), lz, 20, 40);
      var pivot = new THREE.Group(); pivot.position.set(0, cy, 0);
      var m = new THREE.Mesh(g, leafMat); m.position.y = -cy;
      if (s < 0) m.scale.x = -1;
      pivot.add(m); scene.add(pivot); self.occluders.push(m);
      self.leaves.push({ pivot: pivot, s: s });
    });
    var cOpen = (x0 + x1) / 2, cClosed = PANEL_W / 2 - 4;
    this.leafAngle = Math.asin(cOpen / R2) - Math.asin(cClosed / R2);

    /* arch trusses across the opening */
    var archMat = this.mat('arch', { color: 0xf2f4f6, metalness: 0.4, roughness: 0.5, emissive: 0xffffff, emissiveIntensity: 0 });
    [-OPEN_Z, -OPEN_Z / 2.6, OPEN_Z / 2.6, OPEN_Z].forEach(function (z) {
      var pts = [];
      for (var x = -OPEN_W - 14; x <= OPEN_W + 14; x += 4) pts.push(new THREE.Vector3(x, cy + Math.sqrt(R * R - x * x - z * z) + 1.5, z));
      scene.add(new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 40, 1.3, 6), archMat));
    });

    /* lower lattice band (white diagonal struts) between podium and shell */
    var strutGeo = new THREE.CylinderGeometry(0.7, 0.7, 1, 5);
    var N = 84, struts = new THREE.InstancedMesh(strutGeo, archMat, N * 2), m4 = new THREE.Matrix4(), q = new THREE.Quaternion();
    var up = new THREE.Vector3(0, 1, 0), k = 0, yb = 9, yt = SHELL_Y0 + 1, rb = domeRAt(yb) + 2, rt = domeRAt(yt);
    for (var i = 0; i < N; i++) for (var d = -1; d <= 1; d += 2) {
      var a0 = i / N * Math.PI * 2, a1 = a0 + d * Math.PI * 2 / N;
      var p0 = new THREE.Vector3(Math.cos(a0) * rb, yb, Math.sin(a0) * rb), p1 = new THREE.Vector3(Math.cos(a1) * rt, yt, Math.sin(a1) * rt);
      var dir = p1.clone().sub(p0), len = dir.length();
      q.setFromUnitVectors(up, dir.normalize());
      m4.compose(p0.clone().add(p1).multiplyScalar(0.5), q, new THREE.Vector3(1, len, 1));
      struts.setMatrixAt(k++, m4);
    }
    scene.add(struts);

    /* podium: dark bronze louvred base */
    var podTex = canvasTex(THREE, 512, 64, function (g, w, h) {
      g.fillStyle = '#3a3029'; g.fillRect(0, 0, w, h);
      for (var x = 0; x < w; x += 6) { g.fillStyle = x % 12 ? '#5a4a3f' : '#2a221d'; g.fillRect(x, 0, 3, h); }
      g.fillStyle = '#1b1612'; g.fillRect(0, h / 2 - 1, w, 2);
    }, [10, 1]);
    var podium = new THREE.Mesh(new THREE.CylinderGeometry(domeRAt(9) + 3, domeRAt(0) + 6, 9, 96, 1, true), this.mat('podium', { map: podTex, roughness: 0.7, metalness: 0.3, side: THREE.DoubleSide, emissive: 0xffb36b, emissiveIntensity: 0 }));
    podium.position.y = 4.5; scene.add(podium); this.occluders.push(podium);
    var podTop = new THREE.Mesh(new THREE.RingGeometry(110, domeRAt(9) + 3, 96), this.mat('podTop', { color: 0x4a4f45, roughness: 1 }));
    podTop.rotation.x = -Math.PI / 2; podTop.position.y = 9; scene.add(podTop);

    /* bowl */
    var pitchTex = canvasTex(THREE, 512, 800, function (g, w, h) {
      for (var i = 0; i < 16; i++) { g.fillStyle = i % 2 ? '#3f8f2c' : '#4aa233'; g.fillRect(0, i * h / 16, w, h / 16); }
      g.strokeStyle = 'rgba(255,255,255,.85)'; g.lineWidth = 4;
      g.strokeRect(20, 20, w - 40, h - 40);
      g.beginPath(); g.moveTo(20, h / 2); g.lineTo(w - 20, h / 2); g.stroke();
      g.beginPath(); g.arc(w / 2, h / 2, 70, 0, 7); g.stroke();
      g.strokeRect(w / 2 - 150, 20, 300, 120); g.strokeRect(w / 2 - 150, h - 140, 300, 120);
    });
    var pitch = new THREE.Mesh(new THREE.PlaneGeometry(68, 105), this.mat('pitch', { map: pitchTex, roughness: 0.9, emissive: 0xffffff, emissiveMap: pitchTex, emissiveIntensity: 0 }));
    pitch.rotation.x = -Math.PI / 2; pitch.position.y = 0.5; scene.add(pitch);
    var trackTex = canvasTex(THREE, 64, 64, function (g, w, h) {
      g.fillStyle = '#b4553f'; g.fillRect(0, 0, w, h);
      g.fillStyle = 'rgba(255,255,255,.6)'; for (var y = 4; y < h; y += 8) g.fillRect(0, y, w, 1);
    });
    var floor = new THREE.Mesh(bowlRing(THREE, [36, 54, 0.45], [49, 74, 0.45], 96), this.mat('apron', { color: 0x4aa233, roughness: 1 }));
    scene.add(floor);
    scene.add(new THREE.Mesh(bowlRing(THREE, [49, 74, 0.5], [58, 83, 0.5], 128, 1), this.mat('track', { map: trackTex, roughness: 0.95 })));

    var seatTex = canvasTex(THREE, 512, 128, function (g, w, h) {
      g.fillStyle = '#c8202f'; g.fillRect(0, 0, w, h);
      var rnd = mulberry(7);
      for (var y = 0; y < h; y += 5) {
        g.fillStyle = 'rgba(0,0,0,.25)'; g.fillRect(0, y + 3, w, 1);
        for (var x = 0; x < w; x += 4) if (rnd() < 0.18 + 0.25 * Math.sin(x / 40) * Math.sin(y / 20)) { g.fillStyle = '#f1eeee'; g.fillRect(x, y, 3, 3); }
      }
    }, [1, 1]);
    seatTex.wrapS = THREE.RepeatWrapping;
    var seatMat = this.mat('seats', { map: seatTex, roughness: 0.85, side: THREE.DoubleSide, emissive: 0xffffff, emissiveMap: seatTex, emissiveIntensity: 0 });
    var wallMat = this.mat('wall', { color: 0x1a1c1f, roughness: 0.8, side: THREE.DoubleSide });
    var ledMat = this.mat('led', { color: 0x111111, emissive: new THREE.Color(accent), emissiveIntensity: 0.25 });
    var tiers = [[[59, 84, 1], [75, 99, 11]], [[77, 101, 14], [89, 112, 23]], [[91, 114, 26], [103, 124, 37]]];
    var prevTop = 0.5;
    tiers.forEach(function (t, n) {
      scene.add(new THREE.Mesh(bowlRing(THREE, t[0], t[1], 160, 12), seatMat));
      scene.add(new THREE.Mesh(bowlRing(THREE, [t[1][0], t[1][1], t[1][2]], [t[1][0], t[1][1], n === 2 ? 10 : 0], 120), wallMat));
      scene.add(new THREE.Mesh(bowlRing(THREE, [t[0][0], t[0][1], t[0][2]], [t[0][0], t[0][1], prevTop], 120), wallMat));
      // LED ribbon board on the front of tiers 2 + 3
      if (n) scene.add(new THREE.Mesh(bowlRing(THREE, [t[0][0] - 0.3, t[0][1] - 0.3, t[0][2] - 0.3], [t[0][0] - 0.3, t[0][1] - 0.3, t[0][2] - 1.6], 120), ledMat));
      prevTop = t[1][2];
    });
    // giant screens at both ends
    var scr = this.mat('screen', { color: 0x050505, emissive: new THREE.Color(accent), emissiveIntensity: 0.15 });
    [-1, 1].forEach(function (s) {
      var b = new THREE.Mesh(new THREE.BoxGeometry(26, 9, 1.2), scr);
      b.position.set(0, 44, s * 118); scene.add(b);
    });
    // floodlight ring under the roof (visible at night from inside)
    var flPos = [];
    for (var j = 0; j < 140; j++) { var a = j / 140 * Math.PI * 2; flPos.push(Math.cos(a) * 112, 56, Math.sin(a) * 128); }
    var flG = new THREE.BufferGeometry(); flG.setAttribute('position', new THREE.Float32BufferAttribute(flPos, 3));
    this.flood = new THREE.Points(flG, new THREE.PointsMaterial({ color: 0xffffff, size: 2.4, transparent: true, opacity: 0.9 }));
    scene.add(this.flood);
  };

  Venue.prototype.buildPrecinct = function () {
    var THREE = this.T, scene = this.scene, self = this;
    var roofMat = this.mat('roofs', { color: 0xb9c3cc, metalness: 0.15, roughness: 0.5 });
    var blueRoof = this.mat('blueRoof', { color: 0x6f8fb3, metalness: 0.15, roughness: 0.55 });
    var base = this.mat('bldg', { color: 0x4b4540, roughness: 0.8, emissive: 0xffc68a, emissiveIntensity: 0 });
    var glass = this.mat('glass', { color: 0x9fb6c8, metalness: 0.6, roughness: 0.15, transparent: true, opacity: 0.55, side: THREE.DoubleSide, emissive: 0xffffff, emissiveIntensity: 0 });
    function box(w, h, d, x, y, z, m, ry) { var b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); b.position.set(x, y, z); b.rotation.y = ry || 0; scene.add(b); self.occluders.push(b); return b; }

    // OCBC Aquatic Centre — barrel vault (half-cylinder, axis along z)
    box(96, 6, 150, -215, 3, 40, base);
    var vg = new THREE.CylinderGeometry(48, 48, 150, 32, 1, false, Math.PI / 2, Math.PI);
    vg.rotateX(Math.PI / 2);
    var vault = new THREE.Mesh(vg, blueRoof);
    vault.scale.set(1, 0.42, 1); vault.position.set(-215, 6, 40);
    scene.add(vault); self.occluders.push(vault);

    // OCBC Arena — big hall with flat ribbed blue roof, north of the Aquatic Centre
    box(104, 18, 92, -215, 9, -150, base);
    box(108, 3, 96, -215, 19.5, -150, blueRoof);

    // OCBC Square — open courts between the stadium and the OCBC buildings
    var sq = new THREE.Mesh(new THREE.PlaneGeometry(64, 44), this.mat('square', { color: 0xc96a3d, roughness: 1 }));
    sq.rotation.x = -Math.PI / 2; sq.rotation.z = 0.5; sq.position.set(-118, 0.3, -95); scene.add(sq);
    var sqLines = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(28, 15, 2, 1)), new THREE.LineBasicMaterial({ color: 0xffffff }));
    [-15, 15].forEach(function (o) { var l = sqLines.clone(); l.rotation.copy(sq.rotation); l.position.set(-118 + o * Math.cos(0.5), 0.45, -95 - o * Math.sin(0.5)); scene.add(l); });

    // North sports belt: Home of Athletics, Kallang Football Hub, Kallang Tennis Hub
    var grass = this.mat('grass', { color: 0x3f8f2c, roughness: 1, emissive: 0xffffff, emissiveIntensity: 0 });
    var tr = new THREE.Mesh(bowlRing(THREE, [36, 54, 0.3], [46, 64, 0.3], 72), this.mats.track);
    tr.position.set(-250, 0, -330); tr.rotation.y = Math.PI / 2; scene.add(tr);
    var tri = new THREE.Mesh(new THREE.CircleGeometry(1, 48), grass); tri.rotation.x = -Math.PI / 2; tri.scale.set(56, 38, 1); tri.position.set(-250, 0.32, -330); scene.add(tri);
    box(80, 10, 14, -250, 5, -395, base);                                              // athletics stand
    [[-58, -340], [8, -340]].forEach(function (p) {                                   // football hub pitches
      var f = new THREE.Mesh(new THREE.PlaneGeometry(56, 86), self.mats.pitch); f.rotation.x = -Math.PI / 2; f.position.set(p[0], 0.3, p[1]); scene.add(f);
    });
    box(20, 8, 40, 52, 4, -340, base);
    var courtMat = this.mat('court', { color: 0x2f6aa8, roughness: 1 });
    for (var cx = 0; cx < 4; cx++) for (var cz = 0; cz < 3; cz++) {                  // tennis hub courts
      var ct = new THREE.Mesh(new THREE.PlaneGeometry(16, 34), courtMat); ct.rotation.x = -Math.PI / 2;
      ct.position.set(130 + cx * 22, 0.3, -380 + cz * 40); scene.add(ct);
    }
    // Tanjong Rhu MRT — entrance block across the river
    box(28, 9, 16, 610, 4.5, 250, this.mats.portal || this.mat('portal', { color: 0x222326, roughness: 0.6, emissive: 0xffffff, emissiveIntensity: 0 }), 0.3);
    box(2, 14, 2, 628, 7, 262, this.mats.mrtPost || this.mat('mrtPost', { color: 0xd8262f, emissive: 0xd8262f, emissiveIntensity: 0.2 }));

    // Kallang Wave Mall — long low block with green roof + glass front
    box(48, 16, 190, 248, 8, 34, base, -0.28);
    box(50, 2, 192, 248, 17, 34, this.mat('green', { color: 0x4f7a3a, roughness: 1 }), -0.28);
    var front = new THREE.Mesh(new THREE.PlaneGeometry(190, 12), glass);
    front.position.set(222, 7, 27); front.rotation.y = -0.28 - Math.PI / 2; scene.add(front);

    // ETFE canopy between stadium and mall
    var can = new THREE.Mesh(new THREE.PlaneGeometry(80, 46, 8, 4), glass);
    can.rotation.x = -Math.PI / 2 + 0.08; can.position.set(150, 14, 44); can.rotation.z = -0.25; scene.add(can);
    var canFrame = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(80, 46, 4, 2)), new THREE.LineBasicMaterial({ color: 0xffffff }));
    canFrame.rotation.copy(can.rotation); canFrame.position.copy(can.position); canFrame.position.y += 0.2; scene.add(canFrame);

    // Singapore Indoor Stadium — pagoda-style pyramid roof by the water
    box(96, 14, 96, 372, 7, 120, base, Math.PI / 8);
    var py = new THREE.Mesh(new THREE.ConeGeometry(82, 34, 4, 1), roofMat);
    py.position.set(372, 31, 120); py.rotation.y = Math.PI / 4 + Math.PI / 8; scene.add(py); self.occluders.push(py);

    // Stadium MRT entrance — dark angular portal + signpost
    var portal = box(34, 10, 18, 300, 5, -88, this.mat('portal', { color: 0x222326, roughness: 0.6, emissive: 0xffffff, emissiveIntensity: 0 }), 0.4);
    portal.scale.set(1, 1, 1);
    box(2, 14, 2, 322, 7, -74, this.mat('mrtPost', { color: 0xd8262f, emissive: 0xd8262f, emissiveIntensity: 0.2 }));

    // Water Sports Centre + blue pontoons
    box(46, 8, 22, -150, 4, 150, base, 0.15);
    var ponMat = this.mat('pontoon', { color: 0x1f6fd6, roughness: 0.6 });
    [[-160, 198], [-120, 204], [-80, 214]].forEach(function (p) { var b = box(26, 1.2, 7, p[0], 0.4, p[1], ponMat, 0.15); b.castShadow = false; });

    // trees
    var rnd = mulberry(42), tm = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 0), this.mat('tree', { color: 0x2f5b2a, roughness: 1, flatShading: true }), 520);
    var m4 = new THREE.Matrix4(), n = 0, guard = 0;
    while (n < 520 && guard++ < 8000) {
      var a = rnd() * Math.PI * 2, rr = 175 + Math.pow(rnd(), 0.8) * 560, x = Math.cos(a) * rr, z = Math.sin(a) * rr;
      if (z > shore(x) - 18) continue;
      if (x > -275 && x < -155 && z > -205 && z < 120) continue;      // aquatic + arena
      if (x > -160 && x < -75 && z > -130 && z < -60) continue;       // OCBC Square
      if (x > 210 && x < 290 && z > -70 && z < 140) continue;         // mall
      if (x > 270 && x < 335 && z > -110 && z < -65) continue;        // Stadium MRT
      if (x > 310 && x < 435 && z > 55 && z < 185) continue;          // indoor
      if (riverDist(x, z) < 50) continue;                             // river banks
      if (z < -270 && z > -430 && x > -320 && x < 240) continue;      // north sports belt
      var rt = (x + 1100) / 2000, rz = -440 - 60 * Math.sin(Math.PI * rt) + 120 * rt;
      if (Math.abs(z - rz) < 22) continue;                            // road
      var s = 4 + rnd() * 5;
      m4.makeScale(s, s * 1.15, s); m4.setPosition(x, s, z); tm.setMatrixAt(n++, m4);
    }
    tm.count = n; scene.add(tm);
  };

  Venue.prototype.buildCity = function () {
    var THREE = this.T, rnd = mulberry(99), N = 420;
    var winTex = canvasTex(THREE, 64, 128, function (g, w, h) {
      g.fillStyle = '#000'; g.fillRect(0, 0, w, h);
      for (var y = 4; y < h; y += 8) for (var x = 4; x < w; x += 8) if (Math.random() < 0.55) { g.fillStyle = Math.random() < 0.8 ? '#ffd9a0' : '#cfe8ff'; g.fillRect(x, y, 4, 4); }
    });
    var mat = this.mat('city', { color: 0xc9c4ba, roughness: 0.9, emissive: 0xffffff, emissiveMap: winTex, emissiveIntensity: 0 });
    var im = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), mat, N), m4 = new THREE.Matrix4(), n = 0, guard = 0;
    while (n < N && guard++ < 10000) {
      var x, z, h, w;
      if (n < 90) { x = -1100 + rnd() * 700; z = -(-shore(0)) + 520 + rnd() * 380; h = 60 + rnd() * 210; w = 26 + rnd() * 30; }   // CBD across the basin
      else {
        var a = -Math.PI * 0.95 + rnd() * Math.PI * 1.25, rr = 760 + rnd() * 900;
        x = Math.cos(a) * rr; z = Math.sin(a) * rr; h = 25 + rnd() * 95; w = 22 + rnd() * 26;
        if (z > shore(x) - 30 || riverDist(x, z) < 60) continue;
      }
      m4.makeScale(w, h, w * (0.6 + rnd() * 0.6)); m4.setPosition(x, h / 2, z); im.setMatrixAt(n++, m4);
    }
    im.count = n; this.scene.add(im);
  };

  /* ---------- look: day / night + roof ---------- */
  Venue.prototype.applyLook = function () {
    var THREE = this.T, n = this.night, M = this.mats;
    this.scene.background = new THREE.Color(n ? 0x070a10 : 0xbcd7ec);
    this.scene.fog = new THREE.Fog(n ? 0x070a10 : 0xbcd7ec, 900, 3200);
    this.hemi.intensity = n ? 0.28 : 0.7;
    this.hemi.color.set(n ? 0x7a8cff : 0xeaf4ff);
    this.sun.intensity = n ? 0.25 : 1.15;
    this.sun.color.set(n ? 0x8aa0ff : 0xfff2dc);
    this.bowlLight.intensity = n ? 2.4 : 0.6;
    this.renderer.toneMappingExposure = n ? 1.15 : 0.9;
    M.shell.emissiveIntensity = n ? 1.6 : 0;
    M.shell.color.set(n ? 0x8796ad : 0xffffff);
    M.leaf.emissiveIntensity = n ? 0.16 : 0;
    M.bridge.emissiveIntensity = n ? 0.4 : 0;
    M.shelter.emissiveIntensity = n ? 0.25 : 0;
    M.arch.emissiveIntensity = n ? 0.35 : 0;
    M.podium.emissiveIntensity = n ? 0.12 : 0;
    M.pitch.emissiveIntensity = n ? 0.35 : 0;
    M.seats.emissiveIntensity = n ? 0.12 : 0;
    M.led.emissiveIntensity = n ? 1.4 : 0.3;
    M.screen.emissiveIntensity = n ? 0.9 : 0.25;
    M.bldg.emissiveIntensity = n ? 0.08 : 0;
    M.glass.emissiveIntensity = n ? 0.25 : 0;
    M.city.emissiveIntensity = n ? 0.9 : 0;
    M.water.color.set(n ? 0x0f2233 : 0x2c5a78);
    M.land.color.set(n ? 0x141a14 : 0x5b6b4c);
    M.plaza.color.set(n ? 0x3a3c3e : 0x9a9c97);
    M.prom.color.set(n ? 0x4a4842 : 0xc4bdb0);
    this.lamps.visible = n; this.carPoints.visible = n; this.flood.visible = n;
    this.root.classList.toggle('spv-is-night', n);
    var nb = this.root.querySelector('[data-act="night"]');
    if (nb) { nb.querySelector('span').textContent = n ? (this.cfg.t_day || 'Day') : (this.cfg.t_night || 'Night'); nb.setAttribute('aria-label', nb.textContent.trim()); }
    var rb = this.root.querySelector('[data-act="roof"]');
    if (rb) { rb.querySelector('span').textContent = this.roofOpen ? (this.cfg.t_close || 'Close roof') : (this.cfg.t_open || 'Open roof'); rb.setAttribute('aria-label', rb.textContent.trim()); }
  };

  /* ---------- input ---------- */
  Venue.prototype.bindInput = function () {
    var self = this, el = this.renderer.domElement, ptrs = {}, lastPinch = 0, moved = 0;
    el.addEventListener('pointerdown', function (e) { ptrs[e.pointerId] = { x: e.clientX, y: e.clientY }; moved = 0; self.userActed(); el.setPointerCapture(e.pointerId); });
    el.addEventListener('pointermove', function (e) {
      var p = ptrs[e.pointerId]; if (!p) return;
      var ids = Object.keys(ptrs);
      if (ids.length === 1) {
        var dx = e.clientX - p.x, dy = e.clientY - p.y; moved += Math.abs(dx) + Math.abs(dy);
        self.sphGoal.th -= dx * 0.0055;
        self.sphGoal.ph = Math.min(1.48, Math.max(0.12, self.sphGoal.ph - dy * 0.0045));
      } else if (ids.length === 2) {
        p.x = e.clientX; p.y = e.clientY;
        var a = ptrs[ids[0]], b = ptrs[ids[1]], d = Math.hypot(a.x - b.x, a.y - b.y);
        if (lastPinch) self.zoom(lastPinch / d);
        lastPinch = d; return;
      }
      p.x = e.clientX; p.y = e.clientY;
    });
    function up(e) { delete ptrs[e.pointerId]; lastPinch = 0; }
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    // Scroll guard: the page scrolls freely over the model until the visitor
    // clicks/taps it. Then the wheel zooms (lime ring shows it's live). Leaving
    // the stage re-arms the guard, so scrolling past later never zooms it.
    var stage = this.root.querySelector('.spv-stage') || el.parentNode, wheelLive = false;
    stage.addEventListener('pointerdown', function () { wheelLive = true; stage.classList.add('spv-live'); });
    stage.addEventListener('mouseleave', function () { wheelLive = false; stage.classList.remove('spv-live'); });
    el.addEventListener('wheel', function (e) {
      if (!wheelLive) return;
      e.preventDefault(); self.userActed(); self.zoom(1 + Math.max(-0.3, Math.min(0.3, e.deltaY * 0.0012)));
    }, { passive: false });

    this.root.querySelectorAll('[data-act]').forEach(function (b) {
      b.addEventListener('click', function () {
        var a = b.getAttribute('data-act');
        if (a === 'roof') self.toggleRoof();
        if (a === 'night') { self.night = !self.night; self.applyLook(); }
        if (a === 'tour') self.tour ? self.stopTour() : self.startTour();
        if (a === 'reset') { self.stopTour(); self.deselect(); self.goHome(); }
        if (a === 'zin') self.zoom(0.8);
        if (a === 'zout') self.zoom(1.25);
        if (a === 'close') { self.stopTour(); self.deselect(); }
        if (a === 'next') { self.stopTour(); self.select((self.active + 1) % self.hotspots.length); }
        if (a === 'prev') { self.stopTour(); self.select((self.active - 1 + self.hotspots.length) % self.hotspots.length); }
      });
    });
    this.root.querySelectorAll('[data-hs]').forEach(function (b) {
      b.addEventListener('click', function () { self.stopTour(); self.select(+b.getAttribute('data-hs')); });
    });
  };
  Venue.prototype.zoom = function (f) { this.sphGoal.r = Math.min(1300, Math.max(28, this.sphGoal.r * f)); };
  Venue.prototype.userActed = function () { this.idleT = 0; };

  Venue.prototype.flyTo = function (tgt, camPos) {
    var THREE = this.T;
    this.tgtGoal.set(tgt[0], tgt[1], tgt[2]);
    var off = new THREE.Vector3(camPos[0] - tgt[0], camPos[1] - tgt[1], camPos[2] - tgt[2]);
    var r = off.length(), ph = Math.acos(off.y / r), th = Math.atan2(off.x, off.z);
    var cur = this.sphGoal.th, d = th - cur;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    this.sphGoal = { r: r, th: cur + d, ph: Math.min(1.48, Math.max(0.12, ph)) };
  };
  Venue.prototype.goHome = function () {
    var h = this.home;
    this.tgtGoal.set(h.tgt[0], h.tgt[1], h.tgt[2]);
    var cur = this.sphGoal.th, d = Math.atan2(Math.sin(h.th - cur), Math.cos(h.th - cur));
    this.sphGoal = { r: h.r, th: cur + d, ph: h.ph };
    this.moveSpark([-40, 6, 172]);
    this.setSparkPose(this.cfg.spark_idle);
    this.say(this.cfg.intro || "Tap a glowing point and I'll show you around.");
  };

  /* ---------- hotspots + Spark ---------- */
  Venue.prototype.select = function (i) {
    var h = this.hotspots[i]; if (!h) return;
    this.active = i;
    this.hotspots.forEach(function (o) { o.el.classList.toggle('on', o.i === i); });
    this.root.querySelectorAll('[data-hs]').forEach(function (b) { b.classList.toggle('on', +b.getAttribute('data-hs') === i); });
    var camPos = h.cam || [h.p[0] * 1.6 + 120, h.p[1] + 120, h.p[2] * 1.6 + 160];
    if (h.inside && this.roofT > 0.5 && this.cfg.auto_open !== false) this.toggleRoof(true);
    this.flyTo(h.p, camPos);
    // Spark lands beside the hotspot, on the camera's right
    var THREE = this.T, side = new THREE.Vector3(camPos[0] - h.p[0], 0, camPos[2] - h.p[2]).normalize();
    var right = new THREE.Vector3(side.z, 0, -side.x), k = h.inside ? 9 : 22;
    this.moveSpark([h.p[0] + right.x * k + side.x * k * 0.4, h.p[1] + (h.inside ? 2 : 4), h.p[2] + right.z * k + side.z * k * 0.4], h.pose);
    this.say(h.line || h.title);
    this.openPanel(h);
  };
  Venue.prototype.deselect = function () {
    this.active = -1;
    this.hotspots.forEach(function (o) { o.el.classList.remove('on'); });
    this.root.querySelectorAll('[data-hs]').forEach(function (b) { b.classList.remove('on'); });
    this.root.classList.remove('spv-panel-open');
  };
  Venue.prototype.openPanel = function (h) {
    var p = this.root.querySelector('.spv-panel');
    p.querySelector('.spv-p-kick').textContent = h.kicker || '';
    p.querySelector('.spv-p-title').textContent = h.title || '';
    p.querySelector('.spv-p-body').innerHTML = h.body || '';
    var img = p.querySelector('.spv-p-img');
    if (h.image) { img.src = h.image; img.alt = h.title || ''; img.hidden = false; } else img.hidden = true;
    var cta = p.querySelector('.spv-p-cta');
    if (h.cta_label && h.cta_url) { cta.textContent = h.cta_label; cta.href = h.cta_url; cta.hidden = false; } else cta.hidden = true;
    p.querySelector('.spv-p-count').textContent = (h.i + 1) + ' / ' + this.hotspots.length;
    this.root.classList.add('spv-panel-open');
  };
  Venue.prototype.setSparkPose = function (url) {
    var u = url || this.cfg.spark_idle;
    if (u && this.sparkImg.getAttribute('src') !== u) this.sparkImg.src = u;
  };
  Venue.prototype.moveSpark = function (to, pose) {
    this.sparkFrom.copy(this.sparkPos); this.sparkTo.set(to[0], to[1], to[2]);
    this.sparkHop = this.reduce ? 1 : 0;
    this.sparkLandPose = pose || this.cfg.spark_idle;
    if (this.reduce) this.setSparkPose(this.sparkLandPose);
    else if (this.cfg.spark_run) this.setSparkPose(this.cfg.spark_run);
    this.spark.classList.add('hop');
    this.bubble.classList.remove('show');
  };
  Venue.prototype.say = function (text) {
    this.pendingLine = text;
    var b = this.bubble;
    b.querySelector('.spv-bubble-in').textContent = text;
    if (this.sparkHop >= 1) b.classList.add('show');
  };

  Venue.prototype.toggleRoof = function (forceOpen) {
    this.roofOpen = forceOpen === true ? true : !this.roofOpen;
    this.applyLook();
    if (this.active < 0 && forceOpen !== true) this.say(this.roofOpen ? (this.cfg.roof_open_line || 'Roof open, under the stars!') : (this.cfg.roof_close_line || 'Closing the roof — rain or shine, the show goes on.'));
  };

  Venue.prototype.startTour = function () {
    var self = this; if (!this.hotspots.length) return;
    this.root.classList.add('spv-touring');
    var i = this.active + 1 >= this.hotspots.length ? 0 : this.active + 1;
    this.select(i);
    this.tour = setInterval(function () { self.select((self.active + 1) % self.hotspots.length); }, (this.cfg.tour_secs || 7) * 1000);
    this.root.querySelector('[data-act="tour"] span').textContent = this.cfg.t_stop || 'Stop tour';
  };
  Venue.prototype.stopTour = function () {
    if (!this.tour) return;
    clearInterval(this.tour); this.tour = null;
    this.root.classList.remove('spv-touring');
    this.root.querySelector('[data-act="tour"] span').textContent = this.cfg.t_tour || 'Tour with Spark';
  };

  /* ---------- frame ---------- */
  Venue.prototype.resize = function () {
    var w = this.stage.clientWidth, h = this.stage.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.fov = w < 640 ? 52 : 42;
    this.camera.updateProjectionMatrix();
    this.W = w; this.H = h;
  };

  Venue.prototype.loop = function () {
    if (this.raf) return;
    var self = this;
    (function tick() {
      if (!self.visible) { self.raf = 0; return; }
      self.raf = requestAnimationFrame(tick);
      self.update();
    })();
  };

  Venue.prototype.update = function () {
    var THREE = this.T, dt = Math.min(this.clock.getDelta(), 0.05), k = this.reduce ? 1 : 1 - Math.pow(0.002, dt);
    this.frame++;
    this.idleT += dt;

    // gentle auto-orbit when idle + nothing selected
    if (this.cfg.autorotate !== false && !this.reduce && this.idleT > 6 && this.active < 0) this.sphGoal.th += dt * 0.05;

    this.tgt.lerp(this.tgtGoal, k);
    this.sph.r += (this.sphGoal.r - this.sph.r) * k;
    this.sph.th += (this.sphGoal.th - this.sph.th) * k;
    this.sph.ph += (this.sphGoal.ph - this.sph.ph) * k;
    var wantOff = (this.root.classList.contains('spv-panel-open') && this.W > 749) ? Math.min(190, this.W * 0.17) : 0;
    this.vOff = (this.vOff || 0) + (wantOff - (this.vOff || 0)) * k;
    if (Math.abs(this.vOff) > 0.5) this.camera.setViewOffset(this.W, this.H, this.vOff, 0, this.W, this.H); else this.camera.clearViewOffset();
    var s = this.sph, c = this.camera;
    c.position.set(this.tgt.x + s.r * Math.sin(s.ph) * Math.sin(s.th), this.tgt.y + s.r * Math.cos(s.ph), this.tgt.z + s.r * Math.sin(s.ph) * Math.cos(s.th));
    if (c.position.y < 3) c.position.y = 3;
    c.lookAt(this.tgt);

    // roof leaves
    var goal = this.roofOpen ? 0 : 1;
    this.roofT += Math.sign(goal - this.roofT) * Math.min(Math.abs(goal - this.roofT), dt * (this.reduce ? 10 : 0.4));
    var e = this.roofT < 0.5 ? 2 * this.roofT * this.roofT : 1 - Math.pow(-2 * this.roofT + 2, 2) / 2, ang = this.leafAngle * e;
    this.leaves.forEach(function (l) { l.pivot.rotation.z = l.s * ang; });

    // cars
    if (this.carPoints.visible) {
      var pa = this.carPoints.geometry.attributes.position, pts = this.roadPts, n = pts.length - 1;
      for (var i = 0; i < this.cars.length; i++) {
        var car = this.cars[i]; car.t = (car.t + car.v * dt + 1) % 1;
        var f = car.t * n, a = Math.floor(f), b = Math.min(a + 1, n), u = f - a;
        pa.setXYZ(i, pts[a][0] + (pts[b][0] - pts[a][0]) * u, 1.5, pts[a][1] + (pts[b][1] - pts[a][1]) * u + car.lane);
      }
      pa.needsUpdate = true;
    }

    // Spark hop along an arc
    if (this.sparkHop < 1) {
      this.sparkHop = Math.min(1, this.sparkHop + dt / 1.1);
      var t = this.sparkHop, ee = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this.sparkPos.lerpVectors(this.sparkFrom, this.sparkTo, ee);
      this.sparkPos.y += Math.sin(Math.PI * t) * Math.min(60, this.sparkFrom.distanceTo(this.sparkTo) * 0.25);
      if (this.sparkHop >= 1) { this.spark.classList.remove('hop'); this.setSparkPose(this.sparkLandPose); this.bubble.classList.add('show'); }
    }

    this.renderer.render(this.scene, c);
    this.placeDom();
  };

  Venue.prototype.project = function (v) {
    var p = v.clone().project(this.camera);
    return { x: (p.x + 1) / 2 * this.W, y: (1 - p.y) / 2 * this.H, front: p.z < 1 && p.z > -1 };
  };

  Venue.prototype.placeDom = function () {
    var self = this, checkOcc = this.frame % 8 === 0, cam = this.camera.position;
    this.hotspots.forEach(function (h) {
      var s = self.project(h.v), el = h.el;
      if (!s.front) { el.style.visibility = 'hidden'; return; }
      el.style.visibility = '';
      el.style.transform = 'translate3d(' + s.x.toFixed(1) + 'px,' + s.y.toFixed(1) + 'px,0)';
      if (checkOcc) {
        var dir = h.v.clone().sub(cam), dist = dir.length();
        self.raycaster.set(cam, dir.normalize()); self.raycaster.far = dist - 3;
        var hit = self.raycaster.intersectObjects(self.occluders, false).length > 0;
        el.classList.toggle('behind', hit && h.i !== self.active);
      }
    });
    var sp = this.project(this.sparkPos);
    this.spark.style.visibility = sp.front ? '' : 'hidden';
    var sx = Math.max(40, Math.min(this.W - 40, sp.x)), sy = Math.max(160, Math.min(this.H - 6, sp.y));
    this.spark.style.transform = 'translate3d(' + sx.toFixed(1) + 'px,' + sy.toFixed(1) + 'px,0)';
    var flip = sx > this.W * 0.5;
    this.bubble.classList.toggle('left', flip);
    var room = (flip ? sx : this.W - sx) - 50;
    if (room !== this.bRoom) { this.bRoom = room; this.bubble.firstChild.style.maxWidth = Math.max(120, Math.min(240, room)) + 'px'; }
    this.bubble.style.transform = 'translate3d(' + sx.toFixed(1) + 'px,' + sy.toFixed(1) + 'px,0)';
  };

  /* ---------- mount ---------- */
  function mount(root) {
    if (root.__spv) return; root.__spv = true;
    var v = new Venue(root);
    if (!('IntersectionObserver' in window)) return v.boot();
    var io = new IntersectionObserver(function (es) {
      if (es[0].isIntersecting) { io.disconnect(); v.boot(); }
    }, { rootMargin: '300px' });
    io.observe(root);
  }
  function scan() { document.querySelectorAll('[data-sp-venue3d]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  document.addEventListener('shopify:section:load', scan);
})();
